import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useUnreadCount } from "@/features/notifications/api";
import { pendingKey, usePendingRequests } from "@/features/requests/api";
import { useLocale, useT } from "@/shared/i18n/useT";
import { formatNumber } from "@/shared/lib/format";

const MAX_SHOWN = 99;

/**
 * How many requests wait for me, beside "Requests" in the sidebar. A new request reaches me as
 * a notification, so when the unread count moves (live), the number is asked for again.
 */
export function RequestsNavBadge() {
  const t = useT();
  const { locale } = useLocale();
  const queryClient = useQueryClient();
  const pending = usePendingRequests();
  const unread = useUnreadCount().data;
  const seen = useRef(unread);

  useEffect(() => {
    if (seen.current === unread) return;
    seen.current = unread;
    void queryClient.invalidateQueries({ queryKey: pendingKey });
  }, [unread, queryClient]);

  const count = pending.data?.filter((flow) => flow.can_decide).length ?? 0;
  if (count === 0) return null;
  const shown =
    count > MAX_SHOWN ? `${formatNumber(MAX_SHOWN, locale)}+` : formatNumber(count, locale);
  return (
    <span className="ms-auto inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-warning-soft px-1.5 text-[11.5px] font-semibold text-warning ring-1 ring-warning/30 tabular-nums">
      <span aria-hidden="true">{shown}</span>
      <span className="sr-only">{t("requests.nav.waiting", { count })}</span>
    </span>
  );
}
